import { base64ToFile } from 'ext/lib/files';
import { createFetch } from 'ext/lib/api/fetch-client';
import { isAdminFromUserInfo } from 'ext/lib/api/user-info';
import type {
  ApiClient,
  AccountGates,
  UserInfoApiResponse,
  UserInfoResponse,
  Campaign,
  AddTacticContentResult,
} from 'ext/lib/api/types';

interface AuthConfig {
  accountId: string;
  /** Token can be a static string or a function that returns a fresh token */
  token: string | (() => Promise<string> | string);
}

export interface CustomerApiOptions {
  /** Override for Customer API base URL */
  customerApiUrl?: string;
  /** Override for Image Service Lambda URL */
  imageServiceLambdaUrl?: string;
  /** Override for Job API base URL */
  jobApiUrl?: string;
}

export const getCustomerApiEndpoints = (
  auth: AuthConfig,
  options: CustomerApiOptions = {}
): Partial<ApiClient> => {
  const customerApiUrl =
    options.customerApiUrl ?? import.meta.env.CUSTOMER_API_URL;
  const imageServiceLambdaUrl =
    options.imageServiceLambdaUrl ?? import.meta.env.IMAGE_SERVICE_LAMBDA_URL;

  const jobApiUrl = options.jobApiUrl ?? import.meta.env.JOB_API_URL;

  const fetchCustomerAPI = createFetch(`${customerApiUrl}/v1`, auth);
  const fetchCustomerAPIv2 = createFetch(`${customerApiUrl}/v2`, auth);
  const fetchJobAPI = createFetch(`${jobApiUrl}/api`, auth);
  const fetchImageLambdaService = createFetch(`${imageServiceLambdaUrl}`, auth);

  return {
    // gates
    getAccountGates: () =>
      fetchCustomerAPI('gates').then((gates: AccountGates[]) =>
        gates.reduce<AccountGates>((all, gate) => {
          return {
            ...all,
            ...gate,
          };
        }, {})
      ),
    // Auth0 user info endpoint (no accountId in path since we're discovering it)
    getUserInfo: (): Promise<UserInfoResponse> =>
      fetchCustomerAPI('authn/userinfo', {}, '/').then(
        (response: UserInfoApiResponse) => ({
          accountId: response.active_account_user?.account_id ?? null,
          userId: response.id,
          email: response.email,
          fullName: response.full_name,
          isSpoofing: response.is_spoofing,
          isAdmin: isAdminFromUserInfo(response),
        })
      ),
    // user endpoint
    getUser: userId => fetchCustomerAPI(`users/${userId}`, {}, '/'),
    updateUser: (userId, patch) =>
      fetchCustomerAPI(
        `users/${userId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(patch),
        },
        '/'
      ),
    // Flow endpoints
    getFlowsSummary: () => fetchCustomerAPI('flows?summary=true'),
    getFlow: id => fetchCustomerAPI(`flows/${id}`),
    getLaunchpadEligibleFlows: () =>
      fetchCustomerAPI(
        'flows?published=true&widget_filter[]=include&widget_filter[]=page'
      ),
    createFlow: flow =>
      fetchCustomerAPI('flows', {
        method: 'POST',
        body: JSON.stringify(flow),
      }),
    replaceFlow: (id, flow) =>
      fetchCustomerAPI(`flows/${id}/replace-draft`, {
        method: 'POST',
        body: JSON.stringify(flow),
      }),
    updateFlow: (id, patch) =>
      fetchCustomerAPI(`flows/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    createStepGroup: ({ flowId, group }) =>
      fetchCustomerAPI(`flows/${flowId}/stepGroups`, {
        method: 'POST',
        body: JSON.stringify(group),
      }),
    updateStepGroup: ({ flowId, stepType, stepGroupId }, data) =>
      fetchCustomerAPI(
        `flows/${flowId}/stepGroups/${stepType}/${stepGroupId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      ),
    updateStepChild: ({ flowId, stepType, stepGroupId, stepChildId }, data) =>
      fetchCustomerAPI(
        `flows/${flowId}/stepGroups/${stepType}/${stepGroupId}/stepChildren/${stepChildId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      ),
    moveStepGroup: ({ flowId, stepType, stepGroupId }, toIndex) =>
      fetchCustomerAPI(
        `flows/${flowId}/stepGroups/${stepType}/${stepGroupId}/move`,
        {
          method: 'PATCH',
          body: JSON.stringify({ toIndex }),
        }
      ),
    createStepChild: ({ flowId, stepType, stepGroupId, step }) =>
      fetchCustomerAPI(
        `flows/${flowId}/stepGroups/${stepType}/${stepGroupId}/stepChildren`,
        {
          method: 'POST',
          body: JSON.stringify(step),
        }
      ),
    moveStepChild: ({ flowId, stepType, stepGroupId, stepChildId }, toIndex) =>
      fetchCustomerAPI(
        `flows/${flowId}/stepGroups/${stepType}/${stepGroupId}/stepChildren/${stepChildId}/move`,
        {
          method: 'PATCH',
          body: JSON.stringify({ toIndex }),
        }
      ),
    deleteStepGroup: ({ flowId, stepType, stepGroupId }) =>
      fetchCustomerAPI(
        `flows/${flowId}/stepGroups/${stepType}/${stepGroupId}`,
        {
          method: 'DELETE',
        }
      ),
    deleteStepChild: ({ flowId, stepType, stepGroupId, stepChildId }) =>
      fetchCustomerAPI(
        `flows/${flowId}/stepGroups/${stepType}/${stepGroupId}/stepChildren/${stepChildId}`,
        {
          method: 'DELETE',
        }
      ),
    getLayouts: () => fetchCustomerAPI('flows/template'),

    // Experience endpoints
    getExperiencesSummary: (queryOptions = {}) => {
      const { type = 'persistent', platform } = queryOptions;
      const params = new URLSearchParams();
      if (platform) params.append('platform', platform);
      params.append('type', type);
      params.append('summary', 'true');
      return fetchCustomerAPI(`experiences?${params.toString()}`);
    },
    getExperience: id => fetchCustomerAPI(`experiences/${id}`),
    createStep: (experienceId, step) =>
      fetchCustomerAPI(`experiences/${experienceId}/steps`, {
        method: 'POST',
        body: JSON.stringify(step),
      }),
    createExperience: (experience, createOptions = {}) => {
      const { template } = createOptions;
      const queryString = template ? `?template=${template}` : '';
      return fetchCustomerAPI(`experiences${queryString}`, {
        method: 'POST',
        body: JSON.stringify(experience),
      });
    },
    updateExperience: (id, patch, replace = false) =>
      fetchCustomerAPI(
        `experiences/${id}${replace ? '?replace_steps=true' : ''}`,
        {
          method: 'PATCH',
          body: JSON.stringify(patch),
        }
      ),
    updateStep: ({ experienceId, stepId }, delta) =>
      fetchCustomerAPI(`experiences/${experienceId}/steps/${stepId}`, {
        method: 'PATCH',
        body: JSON.stringify(delta),
      }),
    removeStep: (experienceId, stepId) =>
      fetchCustomerAPI(`experiences/${experienceId}/steps/${stepId}`, {
        method: 'DELETE',
      }),
    moveStep: (experienceId, stepId, patch) =>
      fetchCustomerAPI(`experiences/${experienceId}/steps/${stepId}/move`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    getCustomEvents: () => fetchCustomerAPI('custom_events'),
    // The account's event catalog: application events from SDK `track()` calls
    // and Click-to-Track events (prefixed `appcues_custom:`). `showInUi` drops
    // events the account has hidden — and keeps ones with no label record at
    // all, which is what makes a freshly registered name visible. Appcues' own
    // `appcues:` events are filtered at the picker, not here — this stays a
    // faithful read of the API.
    getEventNames: () =>
      fetchCustomerAPI(
        'graphql',
        {
          method: 'POST',
          body: JSON.stringify({
            query: `
            query EventNames($filter: EventNameFilter) {
              eventNames(filter: $filter) {
                name
                lastSeenAt
              }
            }
          `,
            variables: { filter: { showInUi: true } },
          }),
        },
        '/'
      ),
    // Declares a name in the account's catalog so it reaches the picker before
    // the event has ever fired. The name is a body field, not a path segment,
    // so it needs none of `upsertUserPropertyLabel`'s path-traversal guarding.
    // The server trims and validates (reserved prefixes, control characters,
    // 255 chars) and returns the name it stored — see `isRegisterableEventName`
    // in `ext/entities/events`, which mirrors those rules so the picker never
    // offers a create the server would reject.
    registerEventName: (name: string) =>
      fetchCustomerAPI('event_names', {
        method: 'POST',
        body: JSON.stringify({ event_name: name }),
      }),
    createCustomEvent: event =>
      fetchCustomerAPI('custom_events', {
        method: 'POST',
        body: JSON.stringify(event),
      }),
    updateCustomEvent: (id, delta) =>
      fetchCustomerAPI(`custom_events/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(delta),
      }),
    triggerClickstreamBackfill: (
      identifiers: string[],
      eventName: string,
      urls: string[],
      urlPatterns: string[]
    ) =>
      fetchJobAPI('clickstream/backfill', {
        method: 'POST',
        body: JSON.stringify({
          identifiers,
          event_name: eventName,
          urls,
          url_patterns: urlPatterns,
        }),
      }),
    getUserAccounts: userId =>
      fetchCustomerAPI(`users/${userId}/accounts`, {}, '/'),
    despoof: token =>
      fetchCustomerAPI(
        `web/despoof`,
        {
          method: 'POST',
          body: JSON.stringify({ jwt_token: token }),
        },
        '/'
      ),
    getTemplates: () => fetchCustomerAPI(`templates`),
    createTemplate: template =>
      fetchCustomerAPI(`templates`, {
        method: 'POST',
        body: JSON.stringify(template),
      }),
    updateTemplate: ({ templateId, template }) =>
      fetchCustomerAPI(`templates/${templateId}`, {
        method: 'PATCH',
        body: JSON.stringify(template),
      }),
    deleteTemplate: templateId => {
      return fetchCustomerAPI(`templates/${templateId}`, {
        method: 'DELETE',
      });
    },
    getTags: () => fetchCustomerAPIv2(`tags`),
    getThemes: () => fetchCustomerAPI(`themes`),
    updateUsageProperties: (userId, usageProperties) =>
      fetchCustomerAPI(
        `users/${userId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ usageProperties }),
        },
        '/'
      ),

    getUserPropertiesLabels: () =>
      fetchCustomerAPI('profile_attributes/labels'),
    // Upserts the label record, creating it when the attribute has never been
    // seen — which is how an attribute authored in a builder becomes known to
    // the account before any data has been sent for it.
    upsertUserPropertyLabel: (name, label) => {
      // The name is a path segment. encodeURIComponent leaves `.` and `..`
      // intact, which would retarget the request at the collection — and this
      // is a public ApiClient method, so it can't rely on the picker having
      // normalized its input.
      if (!/^\w+$/.test(name)) {
        return Promise.reject(
          new Error(`Invalid profile attribute name: ${name}`)
        );
      }

      return fetchCustomerAPI(`profile_attributes/labels/${name}`, {
        method: 'PATCH',
        body: JSON.stringify({ label }),
      });
    },
    getUserAccountsV2: () => fetchCustomerAPIv2(''),
    // Doc site integrations (knowledge base) endpoints
    getDocSiteIntegrations: () => fetchCustomerAPI('doc_site_integrations'),
    // Entitlements
    getEntitlements: () => fetchCustomerAPIv2('entitlements'),

    // Locales API methods
    getLocales: () => fetchCustomerAPI('locales'),
    createLocale: body =>
      fetchCustomerAPI('locales', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    removeLocale: localeId =>
      fetchCustomerAPI(`locales/${localeId}`, {
        method: 'DELETE',
      }),

    updateLocale: (localeId, body) =>
      fetchCustomerAPI(`locales/${localeId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    autoTranslate: xliff =>
      fetchCustomerAPI('assistant/translate-xliff', {
        method: 'POST',
        body: xliff,
        account: true,
        contentType: 'application/xml',
        parse: false,
      }),
    fetchImages: ({ number = 1, size = 50 }) =>
      fetchCustomerAPI(
        `images?sort_key=created_at&sort_direction=desc&page_number=${number}&page_size=${size}`
      ),
    uploadImage: message => {
      const file = base64ToFile(message);

      const formData = new FormData();
      formData.append('file', file);

      return fetchCustomerAPI(`images/upload`, {
        method: 'POST',
        body: formData,
        ignoreContentType: true,
      });
    },

    // Rule
    getRule: id => fetchCustomerAPI(`rules/${id}`),

    updateRule: (id, delta) =>
      fetchCustomerAPI(`rules/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(delta),
      }),

    // surveys
    getSurveyTypes: () =>
      fetchCustomerAPI(
        'graphql',
        {
          method: 'POST',
          body: JSON.stringify({
            query: `
            query getSurveyTypes {
              surveyTypes {
                id
                name
                surveyFieldTypes {
                  id
                  name
                }
              }
            }
          `,
          }),
        },
        '/'
      ),
    createSurveyEntity: input =>
      fetchCustomerAPI(
        'graphql',
        {
          method: 'POST',
          body: JSON.stringify({
            query: `
              mutation createSurveyEntity($input: CreateSurveyEntityInput!) {
                createSurveyEntity(input: $input) {
                  surveyEntity {
                    id
                    name
                  }
                }
              }
          `,
            variables: {
              input,
            },
          }),
        },
        '/'
      ),
    createSurveyField: input =>
      fetchCustomerAPI(
        'graphql',
        {
          method: 'POST',
          body: JSON.stringify({
            query: `
             mutation createSurveyField($input: CreateSurveyFieldInput!) {
              createSurveyField(input: $input) {
                surveyField {
                  id
                  name
                }
              }
            }
          `,
            variables: {
              input,
            },
          }),
        },
        '/'
      ),

    // campaigns & tactics (Agent Studio)
    // GraphQL-only — see CampaignAndTacticApis.md. The account is derived from
    // the auth token server-side, so no account id is passed.
    // The campaigns query is paginated (server default limit 25). Fetch the
    // first page to learn pageInfo.total, then fan out the remaining pages in
    // parallel so every campaign reaches the dropdown.
    getCampaigns: async filter => {
      const PAGE_SIZE = 100;

      const fetchPage = (offset: number) =>
        fetchCustomerAPI(
          'graphql',
          {
            method: 'POST',
            body: JSON.stringify({
              query: `
                query Campaigns($filter: CampaignFilter, $pageInfo: PageInput) {
                  campaigns(filter: $filter, pageInfo: $pageInfo) {
                    campaigns {
                      id
                      name
                      tactics {
                        id
                        name
                      }
                    }
                    pageInfo {
                      total
                    }
                  }
                }
              `,
              variables: {
                filter: filter ?? {},
                pageInfo: { limit: PAGE_SIZE, offset },
              },
            }),
          },
          '/'
        ) as Promise<{
          data?: {
            campaigns?: {
              campaigns?: Campaign[];
              pageInfo?: { total?: number };
            };
          };
        }>;

      const first = await fetchPage(0);
      const firstBatch = first.data?.campaigns?.campaigns ?? [];
      const total = first.data?.campaigns?.pageInfo?.total ?? firstBatch.length;

      if (total <= PAGE_SIZE) return firstBatch;

      const offsets: number[] = [];
      for (let offset = PAGE_SIZE; offset < total; offset += PAGE_SIZE) {
        offsets.push(offset);
      }

      const rest = await Promise.all(offsets.map(fetchPage));
      return rest.reduce(
        (acc, page) => acc.concat(page.data?.campaigns?.campaigns ?? []),
        firstBatch
      );
    },

    addTacticContent: (tacticId, contentId) =>
      fetchCustomerAPI(
        'graphql',
        {
          method: 'POST',
          body: JSON.stringify({
            query: `
              mutation AddTacticContent($tacticId: UUID4!, $contentId: String!) {
                addTacticContent(tacticId: $tacticId, contentId: $contentId) {
                  success
                  message
                }
              }
            `,
            variables: { tacticId, contentId },
          }),
        },
        '/'
      ).then(
        ({
          data,
        }: {
          data?: { addTacticContent?: AddTacticContentResult };
        }): AddTacticContentResult =>
          data?.addTacticContent ?? {
            success: false,
            message: 'addTacticContent returned no result',
          }
      ),

    // =========================================================================
    // Mobile-Specific Endpoints
    // =========================================================================

    // Account (v2)
    getAccount: () => fetchCustomerAPIv2(''),

    // Mobile Apps
    getApps: () => fetchCustomerAPI('apps'),

    // Mobile Screens
    getScreen: (appId, screenId) =>
      fetchCustomerAPI(`mobile/${appId}/screens/${screenId}`),

    getScreensSummary: appId =>
      fetchCustomerAPI(`mobile/${appId}/screens?summary=true&state=ALL`),

    updateScreen: (appId, screenId, patch) =>
      fetchCustomerAPI(`mobile/${appId}/screens/${screenId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    removeScreen: (appId, screenId) =>
      fetchCustomerAPI(`mobile/${appId}/screens/${screenId}`, {
        method: 'DELETE',
      }),

    // SDK Token
    getSDKToken: () =>
      fetchCustomerAPI(
        'web/custom-session?role=mobile-sdk',
        {
          method: 'POST',
        },
        // Endpoint is not prefixed with /accounts/$account_id/
        '/'
      ),

    // Add step at position (mobile)
    addStep: (experienceId, stepId, body) =>
      fetchCustomerAPI(`experiences/${experienceId}/steps/${stepId}/add`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    // AI Smart Text
    generateSmartText: (prompt, text) =>
      fetchCustomerAPI(`assistant/rewrite?prompt=${prompt}`, {
        method: 'POST',
        body: JSON.stringify({ text }),
      }),

    // Image Lambda Service
    getLambdaTemplateImage: templateId =>
      fetchImageLambdaService(
        `?accountId=${auth.accountId}&templateId=${templateId}`,
        {
          responseType: 'blob',
        },
        // Endpoint is not prefixed with /accounts/$account_id/
        '/'
      ),

    // =========================================================================
    // Experience Themes (Mobile)
    // =========================================================================

    getExperienceThemes: (queryOptions = {}) => {
      const params = new URLSearchParams();
      if (queryOptions.summary) params.append('summary', 'true');
      if (queryOptions.published) params.append('published', 'true');
      const queryString = params.toString();
      return fetchCustomerAPI(
        `experience_themes${queryString ? `?${queryString}` : ''}`
      );
    },

    createExperienceTheme: body =>
      fetchCustomerAPI('experience_themes', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    migrateExperienceTheme: body =>
      fetchCustomerAPI('experience_themes/migrate', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    publishExperienceTheme: themeId =>
      fetchCustomerAPI(`experience_themes/${themeId}/publish`, {
        method: 'POST',
      }),

    getExperienceTheme: themeId =>
      fetchCustomerAPI(`experience_themes/${themeId}`),

    updateExperienceTheme: (themeId, body) =>
      fetchCustomerAPI(`experience_themes/${themeId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    // =========================================================================
    // Content Templates (Mobile Templates & Custom Components)
    // =========================================================================

    getContentTemplates: type =>
      fetchCustomerAPI(`content_templates?type=${type}`),

    createContentTemplate: body =>
      fetchCustomerAPI('content_templates', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    updateContentTemplate: (id, body) =>
      fetchCustomerAPI(`content_templates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    deleteContentTemplate: id =>
      fetchCustomerAPI(`content_templates/${id}`, {
        method: 'DELETE',
      }),

    // =========================================================================
    // Custom Snippets
    // =========================================================================
    // Backend contract owned by customer-api / sc-87811. The `/v1` prefix and
    // `accounts/{accountId}/` segment are applied automatically by
    // fetchCustomerAPI, so only the resource path `snippets` is passed.

    getCustomSnippets: (filters = {}) => {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);
      const queryString = params.toString();
      return fetchCustomerAPI(
        `snippets${queryString ? `?${queryString}` : ''}`
      );
    },

    getCustomSnippet: id => fetchCustomerAPI(`snippets/${id}`),

    createCustomSnippet: body =>
      fetchCustomerAPI('snippets', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    // PUT, not PATCH. Only the draft version is mutable — published snippets
    // are locked snapshots and cannot be updated.
    updateCustomSnippet: (id, body) =>
      fetchCustomerAPI(`snippets/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),

    // Promotes the current draft to published and rebuilds the account's
    // snippet bundle. Takes no body — the server publishes whatever the draft
    // holds, so a draft write must land first. Returns `{ ok, version }`, not
    // a snippet.
    publishCustomSnippet: id =>
      fetchCustomerAPI(`snippets/${id}/publish`, {
        method: 'POST',
      }),

    // Archive (soft delete) → state `ARCHIVED`.
    deleteCustomSnippet: id =>
      fetchCustomerAPI(`snippets/${id}`, {
        method: 'DELETE',
      }),
  };
};
